import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Prompt, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import PromptPopup from '../../../Prompt/PromptComponent';
import { MOVE_ACTIONS } from '../utils';

const ACHMapRouteLeaveNotificationGuardComponent = ({
  allowLeavingPage,
  children,
  closeModal,
  history,
  lastLocation,
  modify,
  paymentType,
  pendingStep,
  setLastLocation,
  setLeavePopup,
  setLeavingPage,
  setPendingStep,
  setSending,
  showLeavePopup,
  stepperTransferExecutors,
  when
}) => {
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const whenShouldBlockNavigation = useCallback(() => {
    if (!modify && !paymentType.length) {
      return false;
    }
    return !allowLeavingPage;
  }, [allowLeavingPage, paymentType, modify]);

  const promptConfirmed = useCallback(() => {
    if (!lastLocation) {
      return;
    }
    setConfirmedNavigation(true);
  }, [lastLocation, setConfirmedNavigation]);

  const promptPopupProps = useMemo(
    () => ({
      pendingStep: {
        onConfirm: () => {
          promptConfirmed();

          setLeavingPage(true);
          setPendingStep(null);

          if (pendingStep === MOVE_ACTIONS.THREE_ONE) {
            return stepperTransferExecutors[MOVE_ACTIONS.THREE_ONE]();
          }

          if (pendingStep === MOVE_ACTIONS.THREE_TWO) {
            setLeavingPage(false);
            return stepperTransferExecutors[MOVE_ACTIONS.THREE_TWO]();
          }
          return null;
        },
        onCancel: () => {
          setLeavingPage(false);
          setPendingStep(null);
          setSending(false);
        }
      },
      leave: {
        onConfirm: () => {
          setLeavePopup(false);
          promptConfirmed();
          return closeModal();
        },
        onCancel: () => {
          setLeavePopup(false);
          setSending(false);
        }
      }
    }),
    [
      setSending,
      setLeavingPage,
      setPendingStep,
      pendingStep,
      stepperTransferExecutors,
      setLeavePopup,
      closeModal,
      promptConfirmed
    ]
  );

  const { onCancel, onConfirm } = pendingStep
    ? promptPopupProps.pendingStep
    : promptPopupProps.leave;
  const show = Boolean(pendingStep) || showLeavePopup;

  const showModal = useCallback(
    (location) => {
      setLeavePopup(true);
      setLastLocation(location);
    },
    [setLeavePopup, setLastLocation]
  );

  const handleBlockedNavigation = useCallback(
    (nextLocation) => {
      if (!confirmedNavigation && whenShouldBlockNavigation(nextLocation)) {
        showModal(nextLocation);
        return false;
      }

      return true;
    },
    [confirmedNavigation, whenShouldBlockNavigation, showModal]
  );

  useEffect(() => {
    if (confirmedNavigation) {
      setConfirmedNavigation(false);
      // Navigate to the previous blocked location
      history.push(lastLocation.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmedNavigation]);

  return (
    <>
      {children}

      <Prompt
        when={show || whenShouldBlockNavigation(lastLocation) || when}
        message={handleBlockedNavigation}
      />
      {show && <PromptPopup onCancel={onCancel} onConfirm={onConfirm} />}
    </>
  );
};

ACHMapRouteLeaveNotificationGuardComponent.propTypes = {
  allowLeavingPage: PropTypes.bool,
  setLeavingPage: PropTypes.func.isRequired,
  setPendingStep: PropTypes.func.isRequired,
  pendingStep: PropTypes.oneOfType([PropTypes.number]),
  stepperTransferExecutors: PropTypes.shape({}).isRequired,
  setLeavePopup: PropTypes.func.isRequired,
  showLeavePopup: PropTypes.bool,
  closeModal: PropTypes.func,
  children: PropTypes.func.isRequired,
  history: PropTypes.objectOf(PropTypes.func),
  when: PropTypes.bool,
  paymentType: PropTypes.arrayOf(PropTypes.string),
  modify: PropTypes.bool.isRequired,
  setLastLocation: PropTypes.func.isRequired,
  lastLocation: PropTypes.oneOfType([
    PropTypes.shape({
      pathname: PropTypes.string
    })
  ]),
  setSending: PropTypes.func
};

ACHMapRouteLeaveNotificationGuardComponent.defaultProps = {
  history: { push() {} },
  when: false,
  paymentType: [],
  lastLocation: null,
  allowLeavingPage: true,
  pendingStep: null,
  showLeavePopup: false,
  closeModal() {},
  setSending() {}
};

export default withRouter(ACHMapRouteLeaveNotificationGuardComponent);
